/**
 * Guards React's DOM mutations against in-browser page translation (#1422).
 *
 * Chrome and Edge translate a page by merging adjacent text, splitting the run
 * and wrapping each segment in nested `<font style="vertical-align: inherit;">`
 * elements. The ORIGINAL `Text` node is either re-parented into that wrapper or
 * detached — but React's fiber still holds it as `stateNode`. The next commit
 * that structurally touches it calls `parent.removeChild(text)` or
 * `parent.insertBefore(node, text)` against a parent that is no longer the
 * node's parent, and the DOM throws `NotFoundError`. React 19 catches that
 * during the commit phase and unwinds to the nearest error boundary — which in
 * this app is the single root `<ErrorBoundary>` in `main.tsx` — so one stale
 * text node takes down the whole portal. See facebook/react#11538 (open since
 * 2017; react-dom 19.2 still calls both methods naked).
 *
 * Only text with SIBLINGS is at risk: `<div>{name}</div>` takes React's
 * single-text-child fast path and never creates a separately tracked `Text`
 * node, while `<div>{busy && <Spinner />}Save</div>` does.
 *
 * The translator itself runs in Chromium's isolated world, so patching
 * `Node.prototype` here does NOT intercept its DOM calls and is not meant to.
 * It intercepts REACT's calls, which run in this realm.
 *
 * Deliberately narrow: the guard only diverges from native when
 * `child.parentNode !== this`, a state that already throws `NotFoundError`
 * 100% of the time. Nothing that works today behaves differently.
 */

export interface TranslationDomRepair {
  op: 'removeChild' | 'insertBefore';
  nodeKind: 'text' | 'element';
  /** `redirected` performs the op on the node's real parent; the others degrade. */
  action: 'redirected' | 'appended' | 'ignored';
}

/** Chrome/Edge stamp `<html>` before they touch any text. */
export function isPageTranslated(): boolean {
  const html = document.documentElement;
  return html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl');
}

let _installed = false;
let _repairCount = 0;
let _warned = false;
let _translationLatched = false;

/**
 * One-way latch: a document that has been translated once stays structurally
 * dirty for the rest of the session, even after "Show original".
 */
function isTranslationActive(): boolean {
  if (_translationLatched) return true;
  if (
    isPageTranslated() ||
    document.querySelector('font[style*="vertical-align"], [_msttexthash]')
  ) {
    _translationLatched = true;
  }
  return _translationLatched;
}

/**
 * Text nodes are always tolerated — a text-node parent mismatch is never a
 * legitimate app bug, it would have crashed either way. Element mismatches are
 * tolerated only once translation is detectable (Chrome reorders elements for
 * languages like German and Japanese), so genuine React/portal bugs still throw
 * on an untranslated page.
 */
function shouldTolerate(node: Node): boolean {
  return node.nodeType === Node.TEXT_NODE || isTranslationActive();
}

/** Number of mutations rescued so far. Exposed for tests and debugging. */
export function getTranslationDomRepairCount(): number {
  return _repairCount;
}

/**
 * Patches `Node.prototype.removeChild` / `insertBefore`. Call once, before the
 * first React commit. Returns an uninstall function (used by tests).
 */
export function installTranslationDomGuard(
  onFirstRepair?: (repair: TranslationDomRepair) => void,
): () => void {
  if (_installed) return () => {};
  _installed = true;

  const nativeRemoveChild = Node.prototype.removeChild;
  const nativeInsertBefore = Node.prototype.insertBefore;

  function report(
    op: TranslationDomRepair['op'],
    node: Node,
    action: TranslationDomRepair['action'],
  ): void {
    _repairCount += 1;
    if (_warned) return;
    _warned = true;
    console.warn(
      `[translation-dom-guard] ${op} targeted a node displaced by browser page translation ` +
        `(${action}). Prefer the portal's own language switcher over Chrome Translate. See #1422.`,
    );
    try {
      onFirstRepair?.({
        op,
        nodeKind: node.nodeType === Node.TEXT_NODE ? 'text' : 'element',
        action,
      });
    } catch {
      // Telemetry must never break the thing it observes.
    }
  }

  /** Drop a `<font>` wrapper that translation left behind with nothing in it. */
  function pruneEmptyWrapper(node: Node): void {
    if (node.nodeName !== 'FONT' || node.hasChildNodes()) return;
    const parent = node.parentNode;
    if (parent) nativeRemoveChild.call(parent, node);
  }

  // Every path returns the `child`/`node` parameter rather than the native's
  // return value: the DOM spec defines both methods as returning exactly the
  // node they were handed, so this is identical at runtime and keeps the
  // generic `T` without a cast.
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode === this || !shouldTolerate(child)) {
      nativeRemoveChild.call(this, child);
      return child;
    }
    const realParent = child.parentNode;
    if (realParent && this.contains(realParent)) {
      // Translation moved it into a <font> still inside the intended parent.
      report('removeChild', child, 'redirected');
      nativeRemoveChild.call(realParent, child);
      pruneEmptyWrapper(realParent);
      return child;
    }
    // Already detached, or moved out of this subtree — React's intent is met.
    report('removeChild', child, 'ignored');
    return child;
  };

  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child === null || child.parentNode === this || !shouldTolerate(child)) {
      nativeInsertBefore.call(this, node, child);
      return node;
    }
    const realParent = child.parentNode;
    if (realParent && this.contains(realParent)) {
      // Anchor lives inside a <font> wrapper in this subtree — inserting there
      // keeps the visual order exactly right.
      report('insertBefore', node, 'redirected');
      nativeInsertBefore.call(realParent, node, child);
      return node;
    }
    // Anchor is gone entirely; append rather than crash. Order may be off.
    report('insertBefore', node, 'appended');
    nativeInsertBefore.call(this, node, null);
    return node;
  };

  return function uninstallTranslationDomGuard(): void {
    Node.prototype.removeChild = nativeRemoveChild;
    Node.prototype.insertBefore = nativeInsertBefore;
    _installed = false;
    _repairCount = 0;
    _warned = false;
    _translationLatched = false;
  };
}
